'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Link from 'next/link';

interface FormData {
  productName: string;
  category: string;
  quantity: string;
  unit: string;
  specifications: string;
  timeline: string;
  budget: string;
  name: string;
  email: string;
  phone: string;
  company: string;
}

const initialForm: FormData = {
  productName: '',
  category: '',
  quantity: '',
  unit: 'units',
  specifications: '',
  timeline: '',
  budget: '',
  name: '',
  email: '',
  phone: '',
  company: '',
};

const categories = [
  'Apparel & Textiles',
  'Electronics & Components',
  'Packaging & Printing',
  'Food & Beverages',
  'Industrial Equipment',
  'Corporate Gifting',
  'Furniture & Home',
  'Health & Beauty',
  'Other',
];

const timelines = [
  'Within 2 weeks',
  '2–4 weeks',
  '1–2 months',
  '2–3 months',
  '3+ months',
  'Flexible',
];

export default function SourcingRequestPage() {
  const [form, setForm] = useState<FormData>(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<FormData>>({});

  const validate = (): boolean => {
    const newErrors: Partial<FormData> = {};
    if (!form.productName.trim()) newErrors.productName = 'Required';
    if (!form.quantity.trim()) newErrors.quantity = 'Required';
    if (!form.timeline) newErrors.timeline = 'Required';
    if (!form.name.trim()) newErrors.name = 'Required';
    if (!form.email.trim()) newErrors.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Invalid email';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      setSubmitted(true);
    }
  };

  return (
    <main className="min-h-screen bg-background">
      <Header />

      <div className="max-w-2xl mx-auto px-6 pt-36 pb-24">
        {submitted ? (
          /* Success State */
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-3">Request Submitted!</h1>
            <p className="text-muted-foreground leading-relaxed mb-2">
              Thank you, <span className="font-semibold text-foreground">{form.name}</span>. We've received your sourcing request.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-10">
              Our team will review your requirements and get back to you within <span className="font-semibold text-foreground">24 hours</span> with relevant supplier quotes.
            </p>
            <Link
              href="/"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-7 py-3 rounded-full text-sm font-semibold hover:bg-primary/90 transition-colors"
            >
              Back to Home
            </Link>
          </div>
        ) : (
          /* Form */
          <>
            <div className="mb-10">
              <p className="text-xs font-semibold text-accent uppercase tracking-widest mb-3">Sourcing Request</p>
              <h1 className="text-3xl md:text-4xl font-extrabold text-foreground tracking-tight mb-3">
                Tell us what you need
              </h1>
              <p className="text-muted-foreground leading-relaxed">
                Fill in your product requirements below. We'll match you with verified suppliers and get back to you within 24 hours — no commitment required.
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate className="space-y-8">
              {/* Product Details */}
              <fieldset className="space-y-5">
                <legend className="text-xs font-bold text-foreground/50 uppercase tracking-widest pb-1 border-b border-border w-full">
                  Product Details
                </legend>

                <div>
                  <label htmlFor="productName" className="block text-sm font-semibold text-foreground mb-1.5">
                    Product / Item Name <span className="text-accent">*</span>
                  </label>
                  <input
                    id="productName"
                    name="productName"
                    type="text"
                    value={form.productName}
                    onChange={handleChange}
                    placeholder="e.g. Custom Printed T-Shirts, Kraft Paper Bags"
                    className={`w-full px-4 py-3 rounded-xl border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
                      errors.productName ? 'border-red-400' : 'border-border'
                    }`}
                  />
                  {errors.productName && <p className="text-xs text-red-500 mt-1">{errors.productName}</p>}
                </div>

                <div>
                  <label htmlFor="category" className="block text-sm font-semibold text-foreground mb-1.5">
                    Category
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={form.category}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                  >
                    <option value="">Select a category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="quantity" className="block text-sm font-semibold text-foreground mb-1.5">
                      Quantity <span className="text-accent">*</span>
                    </label>
                    <input
                      id="quantity"
                      name="quantity"
                      type="text"
                      value={form.quantity}
                      onChange={handleChange}
                      placeholder="e.g. 500"
                      className={`w-full px-4 py-3 rounded-xl border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
                        errors.quantity ? 'border-red-400' : 'border-border'
                      }`}
                    />
                    {errors.quantity && <p className="text-xs text-red-500 mt-1">{errors.quantity}</p>}
                  </div>
                  <div>
                    <label htmlFor="unit" className="block text-sm font-semibold text-foreground mb-1.5">
                      Unit
                    </label>
                    <select
                      id="unit"
                      name="unit"
                      value={form.unit}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                    >
                      <option value="units">Units</option>
                      <option value="pieces">Pieces</option>
                      <option value="kg">Kg</option>
                      <option value="tons">Tons</option>
                      <option value="boxes">Boxes</option>
                      <option value="sets">Sets</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="specifications" className="block text-sm font-semibold text-foreground mb-1.5">
                    Specifications & Requirements
                  </label>
                  <textarea
                    id="specifications"
                    name="specifications"
                    value={form.specifications}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Describe size, material, color, branding, certifications, or any other specific requirements..."
                    className="w-full px-4 py-3 rounded-xl border border-border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all resize-none"
                  />
                </div>
              </fieldset>

              {/* Timeline & Budget */}
              <fieldset className="space-y-5">
                <legend className="text-xs font-bold text-foreground/50 uppercase tracking-widest pb-1 border-b border-border w-full">
                  Timeline & Budget
                </legend>

                <div>
                  <label htmlFor="timeline" className="block text-sm font-semibold text-foreground mb-1.5">
                    Required By <span className="text-accent">*</span>
                  </label>
                  <select
                    id="timeline"
                    name="timeline"
                    value={form.timeline}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-xl border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
                      errors.timeline ? 'border-red-400' : 'border-border'
                    }`}
                  >
                    <option value="">Select timeline</option>
                    {timelines.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  {errors.timeline && <p className="text-xs text-red-500 mt-1">{errors.timeline}</p>}
                </div>

                <div>
                  <label htmlFor="budget" className="block text-sm font-semibold text-foreground mb-1.5">
                    Estimated Budget (optional)
                  </label>
                  <input
                    id="budget"
                    name="budget"
                    type="text"
                    value={form.budget}
                    onChange={handleChange}
                    placeholder="e.g. $2,000 – $5,000"
                    className="w-full px-4 py-3 rounded-xl border border-border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                  />
                </div>
              </fieldset>

              {/* Contact Info */}
              <fieldset className="space-y-5">
                <legend className="text-xs font-bold text-foreground/50 uppercase tracking-widest pb-1 border-b border-border w-full">
                  Your Contact Details
                </legend>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-foreground mb-1.5">
                      Full Name <span className="text-accent">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className={`w-full px-4 py-3 rounded-xl border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
                        errors.name ? 'border-red-400' : 'border-border'
                      }`}
                    />
                    {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-foreground mb-1.5">
                      Company (optional)
                    </label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      value={form.company}
                      onChange={handleChange}
                      placeholder="Your company"
                      className="w-full px-4 py-3 rounded-xl border border-border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-1.5">
                      Email Address <span className="text-accent">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@company.com"
                      className={`w-full px-4 py-3 rounded-xl border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
                        errors.email ? 'border-red-400' : 'border-border'
                      }`}
                    />
                    {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-foreground mb-1.5">
                      Phone (optional)
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 000-0000"
                      className="w-full px-4 py-3 rounded-xl border border-border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                    />
                  </div>
                </div>
              </fieldset>

              <button
                type="submit"
                className="w-full bg-primary text-primary-foreground py-4 rounded-xl text-sm font-bold hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
              >
                Submit Request — We'll Get Back to You
              </button>

              <p className="text-center text-xs text-muted-foreground">
                No commitment required. We'll match you with relevant suppliers within 24 hours.
              </p>
            </form>
          </>
        )}
      </div>

      <Footer />
    </main>
  );
}
